import React, { useMemo, useState } from "react";
import { format, parseISO, differenceInDays } from "date-fns";
import { Clock, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { describeRRule, parseRRule, expandRRule } from "@/utils/rrule-helpers";

interface Schedule {
  id: string;
  startsAt: Date | string;
  durationMinutes: number;
  rrule?: string | null;
  rruleEndsAt?: Date | string | null;
}

interface Shift {
  id: string;
  startsAt: Date | string;
  endsAt: Date | string;
  status?: string;
}

export interface ScheduleSummaryProps {
  schedules?: Schedule[];
  shifts?: Shift[];
  /** When true, uses compact styling */
  compact?: boolean;
  /** Custom className for the container */
  className?: string;
}

interface ScheduleDisplay {
  summary: string;
  dateRange: string;
  timeRange: string;
  hasRecurrence: boolean;
}

/**
 * Parse a schedule and generate a human-readable summary
 */
function getScheduleSummary(schedule: Schedule): ScheduleDisplay {
  const startsAt =
    typeof schedule.startsAt === "string"
      ? parseISO(schedule.startsAt)
      : schedule.startsAt;

  const timeStart = format(startsAt, "h:mm a");

  // Calculate end time from duration
  const endsAt = new Date(
    startsAt.getTime() + schedule.durationMinutes * 60 * 1000,
  );
  const timeEnd = format(endsAt, "h:mm a");
  const timeRange = `${timeStart} - ${timeEnd}`;

  // If no rrule, it's a single occurrence
  if (!schedule.rrule) {
    return {
      summary: format(startsAt, "MMM d, yyyy"),
      dateRange: "",
      timeRange,
      hasRecurrence: false,
    };
  }

  // Parse the rrule to get human-readable description
  const rruleDescription = describeRRule(schedule.rrule);
  const rruleOptions = parseRRule(schedule.rrule);

  // Build date range - check both database field (rruleEndsAt) and parsed RRULE (until)
  let dateRange = format(startsAt, "MMM d");

  // Get end date from either the database field or parsed RRULE
  const endsAtDate = schedule.rruleEndsAt
    ? typeof schedule.rruleEndsAt === "string"
      ? parseISO(schedule.rruleEndsAt)
      : schedule.rruleEndsAt
    : (rruleOptions?.until ?? null);

  if (endsAtDate) {
    dateRange += ` - ${format(endsAtDate, "MMM d, yyyy")}`;

    // Calculate shift count for end-date based schedules
    const shifts = expandRRule(schedule.rrule, startsAt, startsAt, endsAtDate);
    if (shifts.length > 0) {
      dateRange += ` (${shifts.length} shift${shifts.length !== 1 ? "s" : ""})`;
    }
  } else if (rruleOptions?.count) {
    dateRange += ` (${rruleOptions.count} shift${rruleOptions.count !== 1 ? "s" : ""})`;
  } else {
    dateRange += " onwards";
  }

  // Create a more concise summary for common patterns
  let summary = rruleDescription;

  // Customize common patterns for brevity
  if (rruleOptions?.freq === "WEEKLY" && rruleOptions?.byDay) {
    const days = rruleOptions.byDay;
    if (
      days.length === 5 &&
      days.includes("MO") &&
      days.includes("TU") &&
      days.includes("WE") &&
      days.includes("TH") &&
      days.includes("FR")
    ) {
      summary = "Monday-Friday";
    } else if (days.length === 1) {
      const dayNames: Record<string, string> = {
        MO: "Monday",
        TU: "Tuesday",
        WE: "Wednesday",
        TH: "Thursday",
        FR: "Friday",
        SA: "Saturday",
        SU: "Sunday",
      };
      summary = `Every ${dayNames[days[0]]}`;
    } else {
      // Use short day names for multiple days
      const shortDayNames: Record<string, string> = {
        MO: "Mon",
        TU: "Tue",
        WE: "Wed",
        TH: "Thu",
        FR: "Fri",
        SA: "Sat",
        SU: "Sun",
      };
      summary = days.map((d) => shortDayNames[d]).join(", ");
    }
  } else if (rruleOptions?.freq === "DAILY") {
    if (rruleOptions?.interval === 1) {
      summary = "Daily";
    } else {
      summary = `Every ${rruleOptions.interval} days`;
    }
  }

  return {
    summary,
    dateRange,
    timeRange,
    hasRecurrence: true,
  };
}

/**
 * Format individual shifts for the expanded view
 */
function formatShift(shift: Shift): string {
  const startsAt =
    typeof shift.startsAt === "string"
      ? parseISO(shift.startsAt)
      : shift.startsAt;
  const endsAt =
    typeof shift.endsAt === "string" ? parseISO(shift.endsAt) : shift.endsAt;

  const dateStr = format(startsAt, "MMM d, yyyy");
  const startTimeStr = format(startsAt, "h:mm a");
  const endTimeStr = format(endsAt, "h:mm a");

  return `${dateStr} · ${startTimeStr} - ${endTimeStr}`;
}

export function ScheduleSummary({
  schedules,
  shifts = [],
  compact = false,
  className,
}: ScheduleSummaryProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const scheduleDisplays = useMemo(() => {
    if (!schedules || schedules.length === 0) {
      return [];
    }

    // Separate recurring schedules from single-occurrence schedules
    const recurringSchedules: Schedule[] = [];
    const singleSchedules: Schedule[] = [];

    for (const schedule of schedules) {
      if (schedule.rrule) {
        recurringSchedules.push(schedule);
      } else {
        singleSchedules.push(schedule);
      }
    }

    // Group contiguous single-occurrence schedules with the same time/duration
    type ScheduleGroup = {
      schedules: Schedule[];
      timeKey: string; // Used to identify schedules with same time
    };

    const groups: ScheduleGroup[] = [];

    // Sort single schedules by date
    const sortedSingles = [...singleSchedules].sort((a, b) => {
      const dateA =
        typeof a.startsAt === "string" ? parseISO(a.startsAt) : a.startsAt;
      const dateB =
        typeof b.startsAt === "string" ? parseISO(b.startsAt) : b.startsAt;
      return dateA.getTime() - dateB.getTime();
    });

    for (const schedule of sortedSingles) {
      const startsAt =
        typeof schedule.startsAt === "string"
          ? parseISO(schedule.startsAt)
          : schedule.startsAt;
      const timeKey = `${format(startsAt, "HH:mm")}-${schedule.durationMinutes}`;

      // Try to add to an existing group
      let addedToGroup = false;
      for (const group of groups) {
        if (group.timeKey !== timeKey) {
          continue;
        }

        // Check if this schedule is consecutive with the last schedule in the group
        const lastSchedule = group.schedules[group.schedules.length - 1];
        const lastStartsAt =
          typeof lastSchedule.startsAt === "string"
            ? parseISO(lastSchedule.startsAt)
            : lastSchedule.startsAt;

        const daysDiff = differenceInDays(startsAt, lastStartsAt);
        if (daysDiff === 1) {
          group.schedules.push(schedule);
          addedToGroup = true;
          break;
        }
      }

      if (!addedToGroup) {
        groups.push({ schedules: [schedule], timeKey });
      }
    }

    // Convert groups to display format
    const result: {
      id: string;
      summary: string;
      dateRange: string;
      timeRange: string;
      hasRecurrence: boolean;
    }[] = [];

    // Add recurring schedules first
    for (const schedule of recurringSchedules) {
      result.push({
        id: schedule.id,
        ...getScheduleSummary(schedule),
      });
    }

    // Add grouped single schedules
    for (const group of groups) {
      if (group.schedules.length === 1) {
        // Single schedule - use normal summary
        const schedule = group.schedules[0];
        result.push({
          id: schedule.id,
          ...getScheduleSummary(schedule),
        });
      } else {
        // Multiple contiguous schedules - show as date range
        const firstSchedule = group.schedules[0];
        const lastSchedule = group.schedules[group.schedules.length - 1];
        const count = group.schedules.length;

        const firstStartsAt =
          typeof firstSchedule.startsAt === "string"
            ? parseISO(firstSchedule.startsAt)
            : firstSchedule.startsAt;
        const lastStartsAt =
          typeof lastSchedule.startsAt === "string"
            ? parseISO(lastSchedule.startsAt)
            : lastSchedule.startsAt;

        const timeStart = format(firstStartsAt, "h:mm a");
        const endsAt = new Date(
          firstStartsAt.getTime() + firstSchedule.durationMinutes * 60 * 1000,
        );
        const timeEnd = format(endsAt, "h:mm a");

        result.push({
          id: `range-${firstSchedule.id}`,
          summary: `${format(firstStartsAt, "MMM d")} - ${format(lastStartsAt, "MMM d, yyyy")}`,
          dateRange: `${count} shift${count !== 1 ? "s" : ""}`,
          timeRange: `${timeStart} - ${timeEnd}`,
          hasRecurrence: false,
        });
      }
    }

    return result;
  }, [schedules]);

  const formattedShifts = useMemo(() => {
    return shifts.map((shift) => ({
      id: shift.id,
      formatted: formatShift(shift),
      isCanceled: shift.status === "canceled",
    }));
  }, [shifts]);

  const activeShifts = useMemo(
    () => shifts.filter((s) => s.status !== "canceled"),
    [shifts],
  );
  const hasShifts = shifts.length > 0;
  const shiftCount = activeShifts.length;
  const hasSchedules = scheduleDisplays.length > 0;

  // Count total items (shifts take precedence, fall back to schedules)
  const totalItems = hasShifts ? shiftCount : scheduleDisplays.length;

  // Determine if we should show condensed view (more than 2 items)
  const shouldCondense = totalItems > 2;

  // Get date range for condensed view
  const condensedSummary = useMemo(() => {
    if (!shouldCondense) {
      return null;
    }

    // If we have shifts, use active (non-canceled) ones for the summary
    if (hasShifts && activeShifts.length > 0) {
      const sortedShifts = [...activeShifts].sort((a, b) => {
        const dateA =
          typeof a.startsAt === "string" ? parseISO(a.startsAt) : a.startsAt;
        const dateB =
          typeof b.startsAt === "string" ? parseISO(b.startsAt) : b.startsAt;
        return dateA.getTime() - dateB.getTime();
      });

      const firstShift = sortedShifts[0];
      const lastShift = sortedShifts[sortedShifts.length - 1];

      const firstDate =
        typeof firstShift.startsAt === "string"
          ? parseISO(firstShift.startsAt)
          : firstShift.startsAt;
      const lastDate =
        typeof lastShift.startsAt === "string"
          ? parseISO(lastShift.startsAt)
          : lastShift.startsAt;

      const firstEndsAt =
        typeof firstShift.endsAt === "string"
          ? parseISO(firstShift.endsAt)
          : firstShift.endsAt;

      // Check if all shifts have the same time (compare HH:mm of start and end)
      const firstTimeKey = `${format(firstDate, "HH:mm")}-${format(firstEndsAt, "HH:mm")}`;
      const allSameTime = activeShifts.every((shift) => {
        const start =
          typeof shift.startsAt === "string"
            ? parseISO(shift.startsAt)
            : shift.startsAt;
        const end =
          typeof shift.endsAt === "string"
            ? parseISO(shift.endsAt)
            : shift.endsAt;
        return (
          `${format(start, "HH:mm")}-${format(end, "HH:mm")}` === firstTimeKey
        );
      });

      return {
        dateRange: `${format(firstDate, "MMM d")} - ${format(lastDate, "MMM d, yyyy")}`,
        timeRange: allSameTime
          ? `${format(firstDate, "h:mm a")} - ${format(firstEndsAt, "h:mm a")}`
          : "Various times",
        itemCount: shiftCount,
        itemLabel: "shifts",
      };
    }

    // Fall back to schedules for preview (before shifts are materialized)
    if (hasSchedules && schedules && schedules.length > 2) {
      const sortedSchedules = [...schedules].sort((a, b) => {
        const dateA =
          typeof a.startsAt === "string" ? parseISO(a.startsAt) : a.startsAt;
        const dateB =
          typeof b.startsAt === "string" ? parseISO(b.startsAt) : b.startsAt;
        return dateA.getTime() - dateB.getTime();
      });

      const firstSchedule = sortedSchedules[0];
      const lastSchedule = sortedSchedules[sortedSchedules.length - 1];

      const firstDate =
        typeof firstSchedule.startsAt === "string"
          ? parseISO(firstSchedule.startsAt)
          : firstSchedule.startsAt;
      const lastDate =
        typeof lastSchedule.startsAt === "string"
          ? parseISO(lastSchedule.startsAt)
          : lastSchedule.startsAt;

      // Calculate end time from duration
      const firstEndsAt = new Date(
        firstDate.getTime() + firstSchedule.durationMinutes * 60 * 1000,
      );

      // Check if all schedules have the same time (compare HH:mm and duration)
      const firstTimeKey = `${format(firstDate, "HH:mm")}-${firstSchedule.durationMinutes}`;
      const allSameTime = schedules.every((schedule) => {
        const start =
          typeof schedule.startsAt === "string"
            ? parseISO(schedule.startsAt)
            : schedule.startsAt;
        return (
          `${format(start, "HH:mm")}-${schedule.durationMinutes}` ===
          firstTimeKey
        );
      });

      return {
        dateRange: `${format(firstDate, "MMM d")} - ${format(lastDate, "MMM d, yyyy")}`,
        timeRange: allSameTime
          ? `${format(firstDate, "h:mm a")} - ${format(firstEndsAt, "h:mm a")}`
          : "Various times",
        itemCount: schedules.length,
        itemLabel: "shifts",
      };
    }

    return null;
  }, [
    activeShifts,
    schedules,
    shouldCondense,
    hasShifts,
    hasSchedules,
    shiftCount,
  ]);

  if (!hasSchedules && !hasShifts) {
    return null;
  }

  return (
    <div className={cn("flex items-start gap-3", className)}>
      <Clock className="h-5 w-5 text-primary mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        {hasSchedules ? (
          <>
            {/* Condensed view for many shifts */}
            {shouldCondense && condensedSummary ? (
              <>
                <div className="space-y-1">
                  <p className="font-semibold text-base text-gray-900">
                    {condensedSummary.dateRange}
                  </p>
                  {!compact && (
                    <>
                      <p className="text-sm text-gray-600">
                        {condensedSummary.timeRange}
                      </p>
                      <p className="text-sm text-gray-500">
                        {condensedSummary.itemCount}{" "}
                        {condensedSummary.itemLabel}
                      </p>
                    </>
                  )}
                </div>

                {/* Show details button and expandable list */}
                {!compact && (
                  <>
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => setIsExpanded(!isExpanded)}
                      >
                        {isExpanded ? "Hide details" : "View details"}
                      </Button>
                    </div>

                    {/* Expanded details list in grey box */}
                    {isExpanded && (
                      <div className="mt-3 space-y-1.5 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3">
                        {hasShifts
                          ? formattedShifts.map((shift, index) => (
                              <div
                                key={shift.id}
                                className={cn(
                                  "flex items-start gap-2 text-sm",
                                  shift.isCanceled
                                    ? "text-gray-400 line-through"
                                    : "text-gray-700",
                                )}
                              >
                                <span className="text-gray-400 font-mono text-xs w-6 shrink-0 mt-0.5">
                                  {index + 1}.
                                </span>
                                <span>{shift.formatted}</span>
                                {shift.isCanceled && (
                                  <span className="text-xs text-gray-400 font-medium no-underline shrink-0">
                                    Canceled
                                  </span>
                                )}
                              </div>
                            ))
                          : scheduleDisplays.map((schedule, index) => (
                              <div
                                key={schedule.id}
                                className="flex items-start gap-2 text-sm text-gray-700"
                              >
                                <span className="text-gray-400 font-mono text-xs w-6 shrink-0 mt-0.5">
                                  {index + 1}.
                                </span>
                                <span>
                                  {schedule.summary} · {schedule.timeRange}
                                </span>
                              </div>
                            ))}
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              /* Render all schedules in a stacked list (2 or fewer items) */
              <>
                <div
                  className={cn(
                    "space-y-4",
                    scheduleDisplays.length > 1 && "divide-y divide-gray-100",
                  )}
                >
                  {scheduleDisplays.map((scheduleDisplay, index) => (
                    <div
                      key={scheduleDisplay.id}
                      className={cn(index > 0 && "pt-4")}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-base text-gray-900">
                            {scheduleDisplay.summary}
                          </p>
                          {scheduleDisplay.hasRecurrence && (
                            <span className="inline-flex items-center gap-1 text-xs text-primary bg-primary/5 px-2 py-0.5 rounded-full">
                              <Calendar className="h-3 w-3" />
                              Recurring
                            </span>
                          )}
                        </div>
                        {!compact && (
                          <>
                            <p className="text-sm text-gray-600">
                              {scheduleDisplay.timeRange}
                            </p>
                            {scheduleDisplay.dateRange && (
                              <p className="text-sm text-gray-500">
                                {scheduleDisplay.dateRange}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Show total shift count for consistency */}
                {!compact && totalItems > 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    {totalItems} shift{totalItems !== 1 ? "s" : ""} total
                  </p>
                )}
              </>
            )}
          </>
        ) : (
          // Fallback: just show shifts without schedule summary
          <>
            {shouldCondense && condensedSummary ? (
              <div className="space-y-1">
                <p className="font-semibold text-base text-gray-900">
                  {condensedSummary.dateRange}
                </p>
                {!compact && (
                  <>
                    <p className="text-sm text-gray-600">
                      {condensedSummary.timeRange}
                    </p>
                    <p className="text-sm text-gray-500">
                      {condensedSummary.itemCount} {condensedSummary.itemLabel}
                    </p>
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => setIsExpanded(!isExpanded)}
                      >
                        {isExpanded ? "Hide details" : "View details"}
                      </Button>
                    </div>
                    {isExpanded && (
                      <div className="mt-3 space-y-1.5 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3">
                        {formattedShifts.map((shift, index) => (
                          <div
                            key={shift.id}
                            className={cn(
                              "flex items-start gap-2 text-sm",
                              shift.isCanceled
                                ? "text-gray-400 line-through"
                                : "text-gray-700",
                            )}
                          >
                            <span className="text-gray-400 font-mono text-xs w-6 shrink-0 mt-0.5">
                              {index + 1}.
                            </span>
                            <span>{shift.formatted}</span>
                            {shift.isCanceled && (
                              <span className="text-xs text-gray-400 font-medium no-underline shrink-0">
                                Canceled
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <>
                <p className="font-semibold text-base text-gray-900">
                  {shiftCount} shift{shiftCount !== 1 ? "s" : ""}
                </p>
                {!compact && hasShifts && (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto mt-2">
                    {formattedShifts.slice(0, 5).map((shift) => (
                      <div
                        key={shift.id}
                        className={cn(
                          "text-sm",
                          shift.isCanceled
                            ? "text-gray-400 line-through"
                            : "text-gray-700",
                        )}
                      >
                        {shift.formatted}
                        {shift.isCanceled && (
                          <span className="ml-2 text-xs text-gray-400 font-medium no-underline">
                            Canceled
                          </span>
                        )}
                      </div>
                    ))}
                    {shiftCount > 5 && (
                      <p className="text-sm text-gray-500 italic">
                        +{shiftCount - 5} more shifts
                      </p>
                    )}
                  </div>
                )}
                {compact && formattedShifts.length > 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    {formattedShifts[0].formatted}
                  </p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
